import type { components } from "@/api/contract";

export type RelightSettings = components["schemas"]["V2RelightSettings"];
export type EditorLight = RelightSettings["lights"][number];
export const initialLight = (): EditorLight => ({ id: 1, rotation: -45, elevation: 35, intensity: 5, color: "#ffffff", softness: 60 });
export const relightDefaults = (): RelightSettings => ({ revision: "relight-v1", mode: "lights", ambient: 25, lights: [initialLight()] });

export function readRelightSettings(raw: unknown): RelightSettings | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return;
  const s = raw as Record<string, unknown>;
  const integer = (value: unknown, min: number, max: number) => typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
  if (s.revision !== "relight-v1" || !["lights", "reference"].includes(String(s.mode)) || !integer(s.ambient, 0, 100) || !Array.isArray(s.lights)) return;
  if (s.mode === "reference") return s.ambient === 0 && s.lights.length === 0 ? { revision: "relight-v1", mode: "reference", ambient: 0, lights: [] } : undefined;
  if (!s.lights.length || s.lights.length > 4) return;
  const ids = new Set<number>();
  const lights: EditorLight[] = [];
  for (const rawLight of s.lights) {
    if (!rawLight || typeof rawLight !== "object" || Array.isArray(rawLight)) return;
    const light = rawLight as EditorLight;
    if (!integer(light.id, 1, 10000) || ids.has(light.id) || !integer(light.rotation, -180, 180) || !integer(light.elevation, -90, 90) || !integer(light.intensity, 0, 10) || !integer(light.softness, 0, 100) || !/^#[0-9a-fA-F]{6}$/.test(light.color)) return;
    ids.add(light.id);
    lights.push({ id: light.id, rotation: light.rotation, elevation: light.elevation, intensity: light.intensity, color: light.color, softness: light.softness });
  }
  return { revision: "relight-v1", mode: "lights", ambient: s.ambient as number, lights };
}

export function relightSourceIDs(input: Record<string, unknown> | undefined): { image?: string; lighting?: string } {
  const sources = Array.isArray(input?.source_assets) ? input.source_assets : [];
  const id = (index: number, role: string) => {
    const source = sources[index];
    return source && typeof source === "object" && source.role === role && typeof source.id === "string" ? source.id : undefined;
  };
  return { image: id(0, "product"), lighting: id(1, "lighting") };
}
